import { useEffect, useState } from 'react';
import { Button } from '@redpandev/ui';
import { api, ApiError } from '../../lib/api';

const COOLDOWN_SECONDS = 60;

/**
 * « Renvoyer l'email de vérification » — POST /auth/email/verify/request (session).
 * Réponse 202 neutre → confirmation `aria-live` + cooldown local de 60 s.
 * 404 (feature désactivée) → le bouton disparaît sans bruit (dégradation propre).
 * `idleLabel` personnalise le libellé (ex : « Renvoyer le lien » pour un
 * changement d'email en attente — le serveur renvoie alors le lien pendant, spec 07 §3.3).
 */
export function ResendVerificationButton({
  className,
  idleLabel = "Renvoyer l'email de vérification",
}: {
  className?: string;
  idleLabel?: string;
}) {
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const [hidden, setHidden] = useState(false);

  const coolingDown = cooldown > 0;
  useEffect(() => {
    if (!coolingDown) return;
    const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [coolingDown]);

  const handleClick = async () => {
    setSending(true);
    setMessage(null);
    try {
      await api.requestEmailVerification();
      setMessage("📬 C'est parti, regarde ta boîte !");
      setCooldown(COOLDOWN_SECONDS);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // Feature emails désactivée côté serveur : on se retire sans écran d'erreur.
        setHidden(true);
      } else {
        setMessage("Le renvoi n'a pas fonctionné, réessaie dans un instant.");
      }
    } finally {
      setSending(false);
    }
  };

  if (hidden) return null;

  return (
    <div className={className}>
      <Button
        size="sm"
        variant="secondary"
        loading={sending}
        disabled={coolingDown}
        onClick={handleClick}
      >
        {coolingDown ? `Email envoyé (${cooldown} s)` : idleLabel}
      </Button>
      {/* Région live persistante : la confirmation est annoncée aux lecteurs d'écran. */}
      <p aria-live="polite" className="mt-2 min-h-[1.25rem] text-sm text-ink-400">
        {message}
      </p>
    </div>
  );
}
